import { dom } from '@omegagrid/core';
import { FormContainer } from '@omegagrid/form';
import { GridEditEvent } from '@omegagrid/grid';
import {
	AppComponent, GridEditor, ParsedComponentId, SqlEditor, Application,
	SqlEditorResult, SqlEditorEvent } from '@reporting/rp-app-host';
import { JobFormAdapter, JobHistoryGridAdapter, JobStepSqlEditorAdapter, ParamsGridEditorAdapter, SchedulesGridEditorAdapter, AgentSchedulesGridEditorAdapter, JobScheduleGridAdapter } from './adapters';
import { Sidebar } from './components';
import rp from '@reporting/rp-app-host';
import { ETLJob, JobStep } from '@reporting/rp-types/jobs';
import { StepFormAdapter } from './adapters/stepFormAdapter';

const JOBS_SIDEBAR_ID = 'jobs';

const createMainSidebar = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<Sidebar>('rp-jobs-sidebar');
}

const createJobForm = async (app: Application, componentId: ParsedComponentId) => {
	// convert job name back from base64
	const job = componentId.ids[1] ? await rp.ms('jobs').get<ETLJob>('jobs', { job_name: atob(componentId.ids[1]) }) : null;
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new JobFormAdapter(job, rp.accountingProvider.accounting.job_name);
	component.addEventListener('form.save', () => (rp.host.components.get(JOBS_SIDEBAR_ID) as Sidebar)?.refresh());
	return component as unknown as AppComponent;
}

const createStepSqlEditor = async (app: Application, componentId: ParsedComponentId) => {
	const jobName = atob(componentId.ids[1]);
	const stepId = parseInt(componentId.ids[2]);
	// the subsystem decides whether the command is T-SQL (parse/exec) or just text (PowerShell)
	const jobStep = await rp.ms('jobs').get<JobStep>(`steps/${stepId}`, { job_name: jobName });
	const component = dom.createElement<SqlEditor>('rp-editor-sql');
	component.adapter = new JobStepSqlEditorAdapter(jobName, stepId, jobStep?.subsystem);
	component.addEventListener('sqleditor.beforeExec', createSqlEditorBeforeExecHandler(component, app, componentId));
	component.addEventListener('sqleditor.result', createSqlEditorResultHandler(component, app, componentId));
	return component;
}

/** the result tab is per job step: jobs-stepSqlResult-<job_name b64>-<step_id> */
const stepResultId = (componentId: ParsedComponentId) =>
	`${componentId.type}-stepSqlResult-${componentId.ids[1]}-${componentId.ids[2]}`;

const createSqlEditorBeforeExecHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	return () => {
		const resultId = stepResultId(componentId);
		if (app.host.components.has(resultId)) {
			(app.host.components.components.get(resultId) as SqlEditorResult).clear();
		}
	}
}

const createSqlEditorResultHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	// datasets and messages in stream order - a warning belongs in front of the dataset it preceded
	const results: SqlEditorEvent[] = [];
	let resultPromise: Promise<SqlEditorResult> = null;
	const tabColor = rp.settings.get('result.tab.color') === '0' ? null : rp.settings.get('result.tab.color');

	// no query plan tab: the job step editor has no Explain
	return async (e: SqlEditorEvent) => {
		if (e.dataset || e.message) {
			results.push(e);
			const resultId = stepResultId(componentId);
			const openOptions = { title: `${component.name ?? ''} [result]`.trimStart(), color: tabColor, persistent: false };
			let editorResult = app.host.components?.components.get(resultId) as SqlEditorResult;
			if (editorResult) {
				app.host.open(resultId, openOptions);
			} else {
				if (!resultPromise || !app.host.components?.has(resultId)) {
					resultPromise = app.host.open(resultId, openOptions) as Promise<SqlEditorResult>;
				}
				editorResult = await resultPromise;
			}
			if (!editorResult) return;

			let result: SqlEditorEvent;
			while (result = results.shift()) {
				if (result.dataset) {
					if (result.dataset.index == 0) editorResult.clear();
					editorResult.addDataset(result.dataset);
				} else {
					editorResult.addMessage(result.message);
				}
			}
		}
	}
}

const createSqlEditorResult = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SqlEditorResult>('rp-editor-sql-result');
}

const createStepForm = async (app: Application, componentId: ParsedComponentId) => {
	// convert job name back from base64
	const jobName = atob(componentId.ids[1]);
	const stepId = componentId.ids[2] ? parseInt(componentId.ids[2]) : null;
	const jobStep: JobStep = stepId ?
		await rp.ms('jobs').get<JobStep>(`steps/${stepId}`, { job_name: jobName }) :
		{ job_name: jobName };
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new StepFormAdapter(jobStep);
	component.addEventListener('form.save', () => (rp.host.components.get(JOBS_SIDEBAR_ID) as Sidebar)?.refresh());
	return component as unknown as AppComponent;
}

const createParamsEditor = async (app: Application, componentId: ParsedComponentId) => {
	const jobName = atob(componentId.ids[1]);
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ParamsGridEditorAdapter(jobName);
	return component as unknown as AppComponent;
}

const createSchedulesGrid = async (app: Application, componentId: ParsedComponentId) => {
	const jobName = atob(componentId.ids[1]);
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new SchedulesGridEditorAdapter(jobName);
	component.addEventListener('grid.edit', async (e: GridEditEvent) => {
		if (!e.cells) return;
		const listRows = e.cells.filter(c => c.column.data === 'freq_type').map(c => c.row);
		if (listRows.length === 0) return;
		(component.adapter as SchedulesGridEditorAdapter).reloadDayCells(listRows);
	});
	return component;
}

const createHistoryGrid = async (app: Application, componentId: ParsedComponentId) => {
	const jobName = atob(componentId.ids[1]);
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new JobHistoryGridAdapter(jobName);
	return component;
}

const createAgentSchedulesGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new AgentSchedulesGridEditorAdapter(componentId.ids[1] ? parseInt(componentId.ids[1]) : null);
	return component;
}

const createJobScheduleGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new JobScheduleGridAdapter();
	return component;
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['jobs', createMainSidebar],
	['jobForm', createJobForm],
	['plan', createSchedulesGrid],
	['history', createHistoryGrid],
	['params', createParamsEditor],
	['stepSqlEditor', createStepSqlEditor],
	['stepSqlResult', createSqlEditorResult],
	['stepForm', createStepForm],
	['agentPlans', createAgentSchedulesGrid],
	['jobPlans', createJobScheduleGrid],
]);
